/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { CardGrid, SelectCardInput, Button, Checkbox, Spinner, ConfirmationModal } from '@crc-evans/ui-components';
import { Info, Save, Plus, Trash2, X, Shield, ChevronDown, ChevronUp } from 'lucide-react';
import { message } from 'antd';
import axiosConfig from '../../services/axiosConfig';
import { useDispatch, useSelector } from 'react-redux';
import { updateDprSkipState } from '../../redux/dpr/action';

// Root options for Station Rejects (same as Downtime Option dropdown styling)
const ROOT_OPTIONS = ['Root', 'Hot', 'Fills', 'Cap', 'Others'];

import CustomRootSelect from './CustomRootSelect';

// Local Component: CommentBox
const CommentBox = ({ header, value, onChange, placeholder }) => (
  <div style={{ marginTop: '40px' }}>
    <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 500, color: '#64748B', textAlign: 'left' }}>
      {header}
    </h4>
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={6}
      style={{
        width: '100%',
        padding: '16px',
        fontSize: '15px',
        border: '1px solid #E2E8F0',
        borderRadius: '12px',
        resize: 'vertical',
        outline: 'none',
        fontFamily: 'inherit',
        color: '#1F2937',
        backgroundColor: '#FFFFFF'
      }}
    />
  </div>
);

/** Convert display name to camelCase keyName (e.g. "safety error test" -> "safetyErrorTest") */
const toCamelCase = (str) => {
  if (!str || typeof str !== 'string') return '';
  return str
    .trim()
    .split(/\s+|[_-]+/)
    .filter(Boolean)
    .map((word, i) => {
      const w = word.replace(/[^a-zA-Z0-9]/g, '');
      if (!w) return '';
      return i === 0 ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
    })
    .join('');
};

const QualityLog = ({
  qualityData: initialQualityData = [],
  onSave,
  isSubmitting = false,
  shift = 'day',
  dprLogId,
  sowId
}) => {
  const [qualityData, setQualityData] = useState(initialQualityData);
  const [remarks, setRemarks] = useState('');
  const [stationRejects, setStationRejects] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [reportNo, setReportNo] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [newlyAddedId, setNewlyAddedId] = useState(null);
  const dispatch = useDispatch();
  const skipQuality = useSelector(state => {
    if (!sowId) return false;
    return state.dprReducer?.skipStatesBySection?.[sowId]?.skipQuality || false;
  });

  // Modal and Error state
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [newQualityMetric, setNewQualityMetric] = useState({ title: '', value: 0 });
  const [metricError, setMetricError] = useState('');
  const [stationErrors, setStationErrors] = useState({});
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [stationToDelete, setStationToDelete] = useState(null);
  const [isSkipConfirmOpen, setIsSkipConfirmOpen] = useState(false);

  const handleSkipQualityChange = (checked) => {
    if (!sowId) return;
    if (checked) {
      setIsSkipConfirmOpen(true);
    } else {
      dispatch(updateDprSkipState(sowId, 'skipQuality', false));
    }
  };

  const handleConfirmSkipQuality = () => {
    if (sowId) dispatch(updateDprSkipState(sowId, 'skipQuality', true));
    setIsSkipConfirmOpen(false);
  };

  const handleAddQuality = () => {
    setNewQualityMetric({ title: '', value: 0 });
    setMetricError('');
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setNewQualityMetric({ title: '', value: 0 });
  };

  const handleConfirmAddQuality = () => {
    if (!newQualityMetric.title) return;

    // Case-insensitive duplicate check
    const isDuplicate = qualityData.some(item =>
      String(item.title).toLowerCase() === String(newQualityMetric.title).toLowerCase()
    );

    if (isDuplicate) {
      const errorMsg = `Metric "${newQualityMetric.title}" already exists.`;
      message.warning(errorMsg);
      setMetricError(errorMsg);
      return;
    }

    setMetricError('');

    const newMetric = {
      id: `quality-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
      title: newQualityMetric.title,
      month: 0,
      total: 0,
      summary: 0,
      today: 0,
      hasToday: true,
      baseMonth: 0,
      baseTotal: 0,
      originalMetric: {
        keyName: `custom-${Date.now()}`,
        name: newQualityMetric.title
      }
    };

    setQualityData(prev => [...prev, newMetric]);
    setNewlyAddedId(newMetric.id);
    handleCloseModal();
  };

  // Effect to scroll to newly added item
  useEffect(() => {
    if (newlyAddedId) {
      // Small timeout to allow render
      const timer = setTimeout(() => {
        const element = document.getElementById(newlyAddedId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        setNewlyAddedId(null);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [newlyAddedId, qualityData]);

  // Fetch quality data from API when shift changes
  const fetchQualityData = useCallback(async () => {
    if (!dprLogId) return;
    setIsLoading(true);
    try {
      const response = await axiosConfig.get(`/dpr-quality?dprLogId=${dprLogId}&shift=${shift}`);

      let qualityMetricsData = null;
      let stationRejectsData = null;
      let notesData = null;
      let reportNoData = null;

      if (response.data && response.data.data) {
        qualityMetricsData = response.data.data.qualityMetrics;
        stationRejectsData = response.data.data.stationRejects;
        notesData = response.data.data.notes;
        reportNoData = response.data.data.reportNo;
      }

      if (reportNoData) {
        setReportNo(reportNoData);
      }

      // Map qualityMetrics to qualityData format
      if (qualityMetricsData && Array.isArray(qualityMetricsData) && qualityMetricsData.length > 0) {
        const mappedQualityData = qualityMetricsData.map((metric, index) => {
          const currentMonth = metric.toMonth || 0;
          const currentTotal = metric.toDateValue || 0;
          const currentSummary = metric.todayValue || 0;

          return {
            id: `quality-${index}-${metric.id || metric.keyName || Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
            title: metric.name || '',
            month: currentMonth,
            total: currentTotal,
            summary: currentSummary,
            today: currentSummary,
            hasToday: true,
            baseMonth: Math.max(0, currentMonth - currentSummary),
            baseTotal: Math.max(0, currentTotal - currentSummary),
            originalMetric: metric
          };
        });
        setQualityData(mappedQualityData);
      } else {
        setQualityData([]);
      }

      // Map stationRejects
      if (stationRejectsData && Array.isArray(stationRejectsData) && stationRejectsData.length > 0) {
        const mappedStationRejects = stationRejectsData.map((reject, index) => {
          const incomingStationNumber = reject.stationNumber || reject.station || '';
          const incomingStationName = reject.stationName || reject.root || '';

          let rootValue = 'Root';
          let stationNameValue = '';

          // Check if the incoming name is one of the standard options (excluding 'Others' which is a fallback category in UI but here acts as limit)
          // Actually, if backend sends 'Others', it might mean literally 'Others' option selected without text? 
          // But usually 'Others' implies text input. 
          // If incomingStationName is a standard option, set it as root.
          if (ROOT_OPTIONS.includes(incomingStationName) && incomingStationName !== 'Others') {
            rootValue = incomingStationName;
          } else if (incomingStationName) {
            // Treat as custom value directly
            rootValue = incomingStationName;
            stationNameValue = incomingStationName; // Keep consistency
          }

          return {
            id: reject.id || `station-${index}-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
            station: incomingStationNumber,
            stationName: stationNameValue,
            todayValue: reject.todayValue || 0,
            remarks: reject.remarks || '',
            root: rootValue,
            originalReject: reject
          };
        });
        setStationRejects(mappedStationRejects);
      } else {
        setStationRejects([]);
      }

      if (notesData) {
        setRemarks(notesData);
      } else {
        setRemarks('');
      }
    } catch (error) {
      console.error('Error fetching quality data:', error);
      setQualityData([]);
      setStationRejects([]);
      setRemarks('');
    } finally {
      setIsLoading(false);
    }
  }, [shift, dprLogId]);

  useEffect(() => {
    fetchQualityData();
  }, [fetchQualityData]);

  const handleSummaryChange = (itemId, value) => {
    if (!itemId) return;
    const numValue = value === '' ? 0 : parseInt(value, 10) || 0;
    setQualityData(prevData => {
      const updatedData = prevData.map(item => {
        if (String(item.id) === String(itemId)) {
          return {
            ...item,
            summary: numValue,
            month: (item.baseMonth || 0) + numValue,
            total: (item.baseTotal || 0) + numValue
          };
        }
        return { ...item };
      });
      return updatedData;
    });
  };

  const buildQualityMetricsPayload = (currentQualityData = qualityData) => {
    const qualityMetrics = currentQualityData.map((item) => {
      const originalMetric = item.originalMetric || {};
      const keyFromTitle = toCamelCase(item.title || '');
      const keyName = keyFromTitle || originalMetric.keyName || item.id;
      return {
        keyName: typeof keyName === 'string' ? keyName : String(keyName),
        name: item.title || originalMetric.name || '',
        todayValue: item.summary || 0
      };
    });
    return qualityMetrics;
  };

  const buildStationRejectsPayload = (currentRejects = stationRejects) => {
    const rejects = currentRejects.map((reject) => {
      const originalReject = reject.originalReject || {};
      const payload = {
        stationNumber: reject.station || originalReject.station || '',
        todayValue: reject.todayValue || 0,
        remarks: reject.remarks || originalReject.remarks || ''
      };

      if (reject.id && reject.originalReject && reject.originalReject.id) {
        payload.id = reject.id;
      }

      // Logic for stationName: Prefer custom stationName (from "Others"), else use root
      const customStationName = reject.stationName || originalReject.stationName;
      const rootValue = reject.root || originalReject.root;

      if (customStationName) {
        payload.stationName = customStationName;
      } else if (rootValue) {
        payload.stationName = rootValue;
      }

      return payload;
    });
    return rejects;
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (!dprLogId) {
        console.error('dprLogId is required to save quality');
        return;
      }

      const qualityMetrics = buildQualityMetricsPayload();
      const rejects = buildStationRejectsPayload();

      const payload = {
        shift: shift,
        dprQuality: {
          dprLogId: dprLogId,
          reportNo: reportNo || `DPR-${dprLogId}-Q`,
          qualityMetrics: qualityMetrics,
          stationRejects: rejects,
          notes: remarks || ''
        }
      };

      await axiosConfig.put('/dpr-quality', payload);
      await fetchQualityData();

      if (onSave) {
        onSave({
          qualityData,
          stationRejects,
          remarks
        });
      }
    } catch (error) {
      console.error('Error saving quality report:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddStation = () => {
    const newStation = {
      id: `station-new-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
      station: '',
      stationName: '',
      todayValue: 0,
      remarks: '',
      root: 'Root'
    };
    const updatedRejects = [...stationRejects, newStation];
    setStationRejects(updatedRejects);
    updateStationRejectSummary(updatedRejects);
  };

  const handleDeleteStation = (id) => {
    console.log('handleDeleteStation called with id:', id);
    setStationToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setStationToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!stationToDelete) return;

    // Optimistically update UI
    const previousRejects = [...stationRejects];
    const previousQualityData = [...qualityData];

    const updatedRejects = stationRejects.filter(station => String(station.id) !== String(stationToDelete));

    // Calculate updated quality data (Station Reject Summary)
    const totalRejectCount = updatedRejects.reduce((sum, reject) => {
      const value = parseInt(reject?.todayValue, 10);
      return sum + (Number.isNaN(value) ? 0 : value);
    }, 0);

    const sourceData = qualityData.length > 0 ? qualityData : defaultQualityData;
    const updatedQualityData = sourceData.map(item => {
      const title = String(item.title || '').toLowerCase();
      const keyName = String(item.originalMetric?.keyName || '').toLowerCase();
      if (title.includes('station reject') || keyName.includes('station')) {
        return {
          ...item,
          summary: totalRejectCount,
          today: totalRejectCount,
          month: (item.baseMonth || 0) + totalRejectCount,
          total: (item.baseTotal || 0) + totalRejectCount
        };
      }
      return { ...item };
    });

    setStationRejects(updatedRejects);
    setQualityData(updatedQualityData);
    setIsDeleteModalOpen(false);
    setStationToDelete(null);

    // Call API
    if (dprLogId) {
      setIsSaving(true);
      try {
        const qualityMetrics = buildQualityMetricsPayload(updatedQualityData);
        const rejects = buildStationRejectsPayload(updatedRejects);

        const payload = {
          shift: shift,
          dprQuality: {
            dprLogId: dprLogId,
            reportNo: reportNo || `DPR-${dprLogId}-Q`,
            qualityMetrics: qualityMetrics,
            stationRejects: rejects,
            notes: remarks || ''
          }
        };

        await axiosConfig.put('/dpr-quality', payload);
        message.success('Station reject deleted successfully');
        await fetchQualityData();
      } catch (error) {
        console.error('Error deleting station reject:', error);
        message.error('Failed to delete station reject');
        // Revert on error
        setStationRejects(previousRejects);
        setQualityData(previousQualityData);
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleStationChange = (id, field, value) => {
    if (!id) return;

    // Validation for duplicates
    if (field === 'station' || field === 'root' || field === 'stationName') {
      const currentStation = stationRejects.find(s => String(s.id) === String(id));
      if (currentStation) {
        const nextStation = field === 'station' ? value : currentStation.station;
        const nextRoot = field === 'root' ? value : currentStation.root;
        const nextStationName = field === 'stationName' ? value : currentStation.stationName;

        if (nextStation && nextRoot) {
          const isDuplicate = stationRejects.some(s => {
            if (String(s.id) === String(id)) return false;

            const sameStation = String(s.station) === String(nextStation);
            const sameRoot = s.root === nextRoot;

            if (nextRoot === 'Others') {
              return sameStation && sameRoot && s.stationName === nextStationName;
            }

            return sameStation && sameRoot;
          });

          if (isDuplicate) {
            const errorMsg = `Station ${nextStation} with ${nextRoot}${nextRoot === 'Others' ? ' (' + nextStationName + ')' : ''} already exists.`;
            message.warning(errorMsg);
            setStationErrors(prev => ({ ...prev, [id]: errorMsg }));
            return;
          }
        }
      }
    }

    // Clear error if validation passes
    setStationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[id];
      return newErrors;
    });

    setStationRejects(prevRejects => {
      const updatedRejects = prevRejects.map(station => {
        if (String(station.id) === String(id)) {
          if (field === 'root' && value !== 'Others') {
            return { ...station, [field]: value, stationName: '' };
          }
          return { ...station, [field]: value };
        }
        return { ...station };
      });
      updateStationRejectSummary(updatedRejects);
      return updatedRejects;
    });
  };

  // Update the Station Reject summary in Defect Tracking whenever station rejects change
  const updateStationRejectSummary = (rejects) => {
    const total = rejects.reduce((sum, reject) => {
      const value = parseInt(reject?.todayValue, 10);
      return sum + (Number.isNaN(value) ? 0 : value);
    }, 0);

    setQualityData(prevData => {
      const source = prevData.length > 0 ? prevData : defaultQualityData;
      return source.map(item => {
        const title = String(item.title || '').toLowerCase();
        const keyName = String(item.originalMetric?.keyName || '').toLowerCase();
        if (title.includes('station reject') || keyName.includes('station')) {
          return {
            ...item,
            summary: total,
            today: total,
            month: (item.baseMonth || 0) + total,
            total: (item.baseTotal || 0) + total
          };
        }
        return { ...item };
      });
    });
  };

  // Default quality data
  const defaultQualityData = [
    {
      id: 'high-low-reject',
      title: 'High Low Reject',
      month: 0,
      total: 0,
      summary: 0,
      today: 0,
      hasToday: true,
      baseMonth: 0,
      baseTotal: 0
    },
    {
      id: 'station-reject',
      title: 'Station Reject',
      month: 0,
      total: 0,
      summary: 0,
      today: 0,
      hasToday: true,
      baseMonth: 0,
      baseTotal: 0
    },
    {
      id: 'rt-repair',
      title: 'RT Repair',
      month: 0,
      total: 0,
      summary: 0,
      today: 0,
      hasToday: true,
      baseMonth: 0,
      baseTotal: 0
    },
    {
      id: 'rt-cut-off',
      title: 'RT Cut Off',
      month: 0,
      total: 0,
      summary: 0,
      today: 0,
      hasToday: true,
      baseMonth: 0,
      baseTotal: 0
    }
  ];

  const displayData = qualityData.length > 0 ? qualityData : defaultQualityData;

  return (
    <div style={{ position: 'relative' }}>
      {isLoading && (
        <div style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(255,255,255,0.7)',
          zIndex: 1000
        }}>
          <Spinner variant="inline" size="medium" />
        </div>
      )}
      <div>
        {/* White container for all content */}
        <div style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '12px',
          padding: '32px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          marginBottom: '24px'
        }}>
          {/* Simple header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px'
          }}>
            <div style={{
              display: 'flex',
              gap: '12px',
              alignItems: 'flex-start'
            }}>
              <div style={{
                color: '#6366F1',
                backgroundColor: '#EEF2FF',
                padding: '8px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Info size={24} />
              </div>
              <div>
                <h3 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: 700, color: '#0F172A', textAlign: 'left' }}>Quality Control Log</h3>
                <p style={{ margin: 0, fontSize: '14px', color: '#64748B', textAlign: 'left' }}>Record daily weld status and defects.</p>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <Checkbox
                label="Skip Quality"
                checked={skipQuality}
                onChange={handleSkipQualityChange}
                borderColor="#00C9A7"
              />
              <Button
                label="Add Quality"
                variant="primary"
                size="small"
                icon={Plus}
                onClick={handleAddQuality}
              />
            </div>
          </div>

          {/* Two Column Layout: Defect Tracking + Station Rejects */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '24px',
            alignItems: 'start',
            marginBottom: '32px'
          }}>
            {/* Left Column: Defect Tracking Section */}
            <div style={{
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              padding: '20px',
              backgroundColor: '#FFFFFF'
            }}>
              <h4 style={{
                margin: '0 0 16px 0',
                fontSize: '14px',
                fontWeight: 600,
                color: '#1F2937',
                textAlign: 'left',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  backgroundColor: '#FEF3C7',
                  border: '2px solid #F59E0B',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  color: '#F59E0B',
                  fontWeight: 'bold'
                }}>!</span>
                Defect Tracking
              </h4>
              <CardGrid columns={1}>
                {displayData.map((item) => {
                  const title = String(item.title || '').toLowerCase();
                  const keyName = String(item.originalMetric?.keyName || '').toLowerCase();
                  const isStationReject = title.includes('station reject') || keyName.includes('station');

                  return (
                    <SelectCardInput
                      key={item.id}
                      highlightBorderColor="#00C9A7"
                      id={item.id}
                      inputValue={item.summary || 0}
                      onClick={() => { }}
                      onInputChange={(val) => handleSummaryChange(item.id, val)}
                      disabled={isStationReject}
                      pills={[
                        { label: 'MONTH', value: item.month || 0 },
                        { label: 'TOTAL', value: item.total || 0 }
                      ]}
                      title={item.title}
                    />
                  );
                })}
              </CardGrid>
            </div>

            {/* Right Column: Station Rejects Section */}
            <div style={{
              border: '1px solid #FEE2E2',
              borderRadius: '8px',
              padding: '20px',
              backgroundColor: '#FFF5F5'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#DC2626', textAlign: 'left' }}>
                  Station Rejects
                </h4>
                <Button
                  label="Add Station"
                  variant="tertiary"
                  size="small"
                  icon={Plus}
                  onClick={handleAddStation}
                  textColor="#DC2626"
                />
              </div>

              {stationRejects.length === 0 ? (
                <div style={{
                  color: '#94A3B8',
                  fontSize: '14px',
                  textAlign: 'center',
                  padding: '40px 20px',
                  backgroundColor: '#FFFFFF',
                  borderRadius: '6px'
                }}>
                  No station rejects recorded.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {stationRejects.map((station, index) => (
                    <div
                      key={station.id}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        backgroundColor: '#FFFFFF',
                        borderRadius: '6px',
                        border: '1px solid #FEE2E2',
                        overflow: 'visible'
                      }}
                    >
                      {/* Main Row */}
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '12px'
                        }}
                      >
                        <div style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '50%',
                          backgroundColor: '#FEE2E2',
                          color: '#DC2626',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          fontWeight: 600,
                          flexShrink: 0
                        }}>
                          {index + 1}
                        </div>

                        <select
                          value={station.station || ''}
                          onChange={(e) => handleStationChange(station.id, 'station', e.target.value)}
                          style={{
                            width: '160px',
                            padding: '8px 10px',
                            fontSize: '13px',
                            border: '1px solid #E5E7EB',
                            borderRadius: '6px',
                            backgroundColor: '#FFFFFF',
                            color: '#374151',
                            outline: 'none'
                          }}
                        >
                          <option value="">Station No.</option>
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                            <option key={num} value={String(num)}>{num}</option>
                          ))}
                        </select>

                        <CustomRootSelect
                          value={station.root || ''}
                          onChange={(val) => handleStationChange(station.id, 'root', val)}
                          placeholder="Root"
                        />



                        <input
                          type="number"
                          min={0}
                          placeholder="Qty"
                          value={station.todayValue}
                          onChange={(e) => handleStationChange(station.id, 'todayValue', e.target.value === '' ? 0 : parseInt(e.target.value, 10) || 0)}
                          style={{
                            width: '100px',
                            padding: '7px 10px',
                            fontSize: '13px',
                            border: '1px solid #E5E7EB',
                            borderRadius: '6px',
                            backgroundColor: '#FFFFFF',
                            color: '#374151',
                            outline: 'none',
                            boxSizing: 'border-box'
                          }}
                        />

                        <button
                          onClick={() => handleDeleteStation(station.id)}
                          style={{
                            padding: '6px',
                            backgroundColor: 'transparent',
                            border: 'none',
                            color: '#EF4444',
                            cursor: 'pointer',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.backgroundColor = '#FEE2E2';
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>

                      {/* Station Name Input - Appears below when Others is selected, aligned with Station No. */}

                      {stationErrors[station.id] && (
                        <div style={{
                          padding: '0 12px 8px 44px',
                          color: '#EF4444',
                          fontSize: '12px',
                          textAlign: 'left'
                        }}>
                          {stationErrors[station.id]}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <CommentBox
            header="Quality Notes / Remarks"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Enter any additional details regarding quality control, defects, or station rejects..."
          />

          {/* Save button sticky at bottom */}
          <div style={{
            position: 'sticky',
            bottom: '24px',
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: '24px',
            paddingTop: '16px',
            zIndex: 10
          }}>
            <Button
              label="Save Quality Report"
              variant="secondary"
              size="large"
              icon={Save}
              onClick={handleSave}
              disabled={isSaving}
            />
          </div>
        </div>
      </div>
      {isModalVisible && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          paddingTop: '100px',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            width: '500px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{
              padding: '24px',
              borderBottom: '1px solid #E5E7EB',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start'
            }}>
              <div style={{ display: 'flex', gap: '16px' }}>
                <div style={{
                  backgroundColor: '#EEF2FF', // Light Indigo
                  padding: '10px',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#4F46E5' // Indigo 600
                }}>
                  <Shield size={24} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' }}>
                    Add Quality Metric
                  </h3>
                  <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6B7280' }}>
                    Add to quality control log
                  </p>
                </div>
              </div>
              <button
                onClick={handleCloseModal}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#9CA3AF',
                  padding: '4px'
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#374151',
                  textAlign: 'left'
                }}>
                  Quality Type <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g., Weld Defect"
                  value={newQualityMetric.title}
                  onChange={(e) => setNewQualityMetric({ ...newQualityMetric, title: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '6px',
                    border: '1px solid #D1D5DB',
                    fontSize: '14px',
                    outline: 'none',
                    color: '#1F2937',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#4F46E5'}
                  onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
                />
              </div>

              {metricError && (
                <div style={{
                  color: '#EF4444',
                  fontSize: '13px',
                  textAlign: 'left',
                  marginTop: '-12px',
                  paddingLeft: '4px'
                }}>
                  {metricError}
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{
              padding: '16px 24px',
              backgroundColor: '#F9FAFB',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              borderTop: '1px solid #E5E7EB'
            }}>
              <Button
                label="Cancel"
                variant="tertiary"
                onClick={handleCloseModal}
                size="medium"
              />
              <Button
                label="Add Metric"
                variant="primary"
                onClick={handleConfirmAddQuality}
                disabled={!newQualityMetric.title}
                size="medium"
              />
            </div>
          </div>
        </div>
      )}
      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        title="Delete Item"
        message="Are you sure you want to delete this item? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        isOpen={isDeleteModalOpen}
        size="medium"
        confirmButtonProps={{
          style: {
            backgroundColor: '#EF4444',
            borderColor: '#EF4444'
          }
        }}
      />
      <ConfirmationModal
        title="Skip Quality"
        message={`Are you sure you want to skip the Quality log for ${shift === 'night' ? 'night' : 'day'} shift?`}
        confirmText="Confirm"
        cancelText="Cancel"
        isOpen={isSkipConfirmOpen}
        onConfirm={handleConfirmSkipQuality}
        onCancel={() => setIsSkipConfirmOpen(false)}
        size="medium"
      />
    </div>
  );
};

export default QualityLog;
